import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TabState } from '../../../../types';
import { ColumnStatistics } from '../../statistics/ColumnStatistics';
import { useProjectData } from "../../../stores/hooks/useProjectData"
import { useDatasetSelection } from "../../../stores/hooks/useDatasetSelection"
import { usePaneLayout } from "../../../stores/hooks/usePaneLayout"
import { useReportActions } from '../../../contexts/ReportContext';
import { findActiveDatasetIdForTab } from '../../../utils/activeDatasetUtils';
import * as styles from '../../../tab-system/TabContainer.css.ts';

interface StatisticsTabContentProps {
  tab: TabState;
}

export const StatisticsTabContent: React.FC<StatisticsTabContentProps> = ({
  tab
}) => {
  // すべてのフックを最初に定義
  const [activeSection, setActiveSection] = useState<{
    sectionType?: 'settings' | 'dataset-overview' | 'column-statistics';
    groupKey?: string;
    columnName?: string;
  }>({ sectionType: 'settings' });
  const [collapsedStates] = useState({
    datasetOverview: false,
    columnStatistics: false
  });
  const [collapsedColumnStats, setCollapsedColumnStats] = useState<Record<string, boolean>>({});
  const [groupByColumns, setGroupByColumns] = useState<string[]>([]);
  const [settingsCollapsed, setSettingsCollapsed] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Context APIから必要なデータを取得
  const { getDataset } = useProjectData();
  const { layout } = usePaneLayout();

  // アクティブなデータセットIDを取得（共通ユーティリティを使用）
  const activeDatasetId = findActiveDatasetIdForTab(layout, tab.id);

  const dataset = activeDatasetId ? getDataset(activeDatasetId) : undefined;

  // DataTableSelectionContextから選択状態を取得
  const {
    selectedColumns = [],
    selectedRows = [],
    selectRows
  } = useDatasetSelection(activeDatasetId || undefined);


  // ハンドラー
  const onToggleDatasetOverview = () => {};
  const onToggleColumnStatistics = () => {};
  const onToggleColumnStats = (columnName: string) => {
    setCollapsedColumnStats(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // ヒストグラムのビンクリック時のハンドラー
  const onHistogramBinClick = useCallback((_minValue: number, _maxValue: number, dataIndices: number[], isAdditive?: boolean) => {
    if (!activeDatasetId) return;


    if (isAdditive) {
      // 加算モード: 既存の選択に追加
      const newSelection = [...new Set([...selectedRows, ...dataIndices])];
      selectRows(newSelection);
    } else {
      // 置換モード: 新しい選択で置き換え
      selectRows(dataIndices);
    }
  }, [activeDatasetId, selectedRows, selectRows]);

  // 行選択ハンドラー
  const onRowSelect = useCallback((rowIndices: number[]) => {
    if (!activeDatasetId) return;


    selectRows(rowIndices);
  }, [activeDatasetId, selectRows]);

  // レポート関連の機能（Contextから取得）
  const {
    availableReports,
    onAddToReport,
    onCreateNewReport
  } = useReportActions();

  const filteredRowIndices: number[] | undefined = undefined;

  // スクロール位置に基づいてアクティブセクションを更新
  useEffect(() => {
    // 要素のスクロールコンテナからの累積offsetTopを計算
    const getOffsetTop = (element: HTMLElement, container: HTMLElement): number => {
      let offset = 0;
      let current: HTMLElement | null = element;
      while (current && current !== container) {
        offset += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
      }
      return offset;
    };

    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const scrollTop = container.scrollTop;
      const threshold = 150; // セクション検出の閾値（px）

      // 各セクションの位置を取得
      const columnStatsSection = container.querySelector('[data-section-type="column-statistics"]') as HTMLElement;
      const datasetOverviewSection = container.querySelector('[data-section-type="dataset-overview"]') as HTMLElement;

      // 下から上へチェック（Column Statistics -> Dataset Overview -> Settings）
      // scrollTopが各セクションの開始位置を超えたら、そのセクションをアクティブにする
      if (columnStatsSection) {
        const colStatsTop = getOffsetTop(columnStatsSection, container);
        if (scrollTop >= colStatsTop - threshold) {
          // Column Statistics内の詳細を処理
          let foundSpecific = false;

          // グループとカラムを探す
          const groupSections = container.querySelectorAll('[data-section-group]');
          for (const groupSection of groupSections) {
            const groupEl = groupSection as HTMLElement;
            const groupTop = getOffsetTop(groupEl, container);
            if (scrollTop >= groupTop - threshold && scrollTop < groupTop + groupEl.offsetHeight) {
              const groupKey = groupSection.getAttribute('data-section-group') || '';

              // カラムを探す
              const columnSections = groupSection.querySelectorAll('[data-section-column]');
              for (const colSection of columnSections) {
                const colEl = colSection as HTMLElement;
                const colTop = getOffsetTop(colEl, container);
                if (scrollTop >= colTop - threshold && scrollTop < colTop + colEl.offsetHeight) {
                  const columnName = colSection.getAttribute('data-section-column') || '';
                  setActiveSection({ sectionType: 'column-statistics', groupKey, columnName });
                  foundSpecific = true;
                  break;
                }
              }

              if (!foundSpecific) {
                setActiveSection({ sectionType: 'column-statistics', groupKey });
                foundSpecific = true;
              }
              break;
            }
          }

          if (!foundSpecific) {
            setActiveSection({ sectionType: 'column-statistics' });
          }
          return;
        }
      }

      // Dataset Overviewセクションをチェック
      if (datasetOverviewSection) {
        const datasetOverviewTop = getOffsetTop(datasetOverviewSection, container);
        if (scrollTop >= datasetOverviewTop - threshold) {
          setActiveSection({ sectionType: 'dataset-overview' });
          return;
        }
      }

      // Settingsセクション（デフォルト）
      setActiveSection({ sectionType: 'settings' });
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // 初回実行
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [groupByColumns.length]);

  // データセットが無い場合はプレースホルダーを表示
  if (!dataset) {
    return (
      <div data-testid="statistics-content" className={styles.tabContentScrollable}>
        <div className={styles.statisticsTabContent}>
          <p>No dataset loaded. Please import a dataset first.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="statistics-content" className={styles.tabContentScrollable}>
      {/* Sticky breadcrumb header */}
      <div className={styles.stickyBreadcrumb}>
        <div className={styles.breadcrumbHeader}>
          <span className={styles.breadcrumbDataset}>Dataset: {dataset.name}</span>
          {selectedRows && selectedRows.length > 0 && (
            <span className={styles.breadcrumbSelected}>({selectedRows.length} selected)</span>
          )}
        </div>
        <div className={styles.breadcrumbPath}>
          <span className={styles.breadcrumbItem}>Statistics</span>
          {activeSection.sectionType === 'settings' && (
            <>
              <span className={styles.breadcrumbSeparator}>›</span>
              <span className={styles.breadcrumbItem}>Settings</span>
            </>
          )}
          {activeSection.sectionType === 'dataset-overview' && (
            <>
              <span className={styles.breadcrumbSeparator}>›</span>
              <span className={styles.breadcrumbItem}>Dataset Overview</span>
            </>
          )}
          {activeSection.sectionType === 'column-statistics' && (
            <>
              <span className={styles.breadcrumbSeparator}>›</span>
              <span className={styles.breadcrumbItem}>Column Statistics</span>
              {groupByColumns.length > 0 && (
                <>
                  <span className={styles.breadcrumbSeparator}>›</span>
                  <span className={styles.breadcrumbItem}>(by {groupByColumns.join(', ')})</span>
                </>
              )}
              {activeSection.groupKey && groupByColumns.length > 0 && activeSection.groupKey !== '' && (
                <>
                  <span className={styles.breadcrumbSeparator}>›</span>
                  <span className={styles.breadcrumbItem}>{activeSection.groupKey}</span>
                </>
              )}
              {activeSection.columnName && (
                <>
                  <span className={styles.breadcrumbSeparator}>›</span>
                  <span className={styles.breadcrumbItem}>{activeSection.columnName}</span>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div ref={scrollContainerRef} className={styles.statisticsTabContent}>
        {/* Settings Section */}
        <div className={styles.statisticsSection} data-section-type="settings">
          <h3
            className={`${styles.collapsibleHeader} ${settingsCollapsed ? styles.collapsed : ''}`}
            onClick={() => setSettingsCollapsed(!settingsCollapsed)}
          >
            Settings
          </h3>
          {!settingsCollapsed && (
            <div className={styles.sectionContent}>
              <div className={styles.settingsSubsection}>
                <h4 className={styles.settingsSubtitle}>Show stats by</h4>
                <div className={styles.groupBySettings}>
                  {dataset && dataset.schema.columns.length > 0 && (
                    <div className={styles.columnSelector}>
                      <select
                        multiple
                        className={styles.multiSelect}
                        value={groupByColumns}
                        onChange={(e) => {
                          const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
                          setGroupByColumns(selectedValues);
                        }}
                      >
                        {dataset.schema.columns
                          .filter(column => column.type !== 'float64')
                          .map(column => (
                            <option key={column.name} value={column.name}>
                              {column.name} ({column.type})
                            </option>
                          ))}
                      </select>
                      <div className={styles.selectorHint}>
                        Hold Ctrl/Cmd to select multiple columns
                      </div>
                    </div>
                  )}
                  {groupByColumns.length > 0 && (
                    <div className={styles.selectedGroupBy}>
                      <span className={styles.selectedLabel}>Selected grouping variables:</span>
                      <div className={styles.selectedTags}>
                        {groupByColumns.map(column => (
                          <span key={column} className={styles.selectedTag}>
                            {column}
                            <button
                              className={styles.removeTag}
                              onClick={() => setGroupByColumns(prev => prev.filter(c => c !== column))}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Dataset Overview Section */}
        <div className={styles.statisticsSection} data-section-type="dataset-overview">
          <h3
            className={`${styles.collapsibleHeader} ${collapsedStates.datasetOverview ? styles.collapsed : ''}`}
            onClick={() => {
              onToggleDatasetOverview();
            }}
          >
            Dataset Overview
          </h3>
          {!collapsedStates.datasetOverview && (
            <div className={styles.sectionContent}>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Total rows:</span>
                  <span className={styles.statValue}>{(dataset.rowCount || 0).toLocaleString()}</span>
                </div>
                {filteredRowIndices && (filteredRowIndices as number[]).length !== (dataset.rowCount || 0) && (
                  <>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Filtered rows:</span>
                      <span className={styles.statValue}>{(filteredRowIndices as number[]).length.toLocaleString()}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Filter ratio:</span>
                      <span className={styles.statValue}>{(((filteredRowIndices as number[]).length / (dataset.rowCount || 1)) * 100).toFixed(1)}%</span>
                    </div>
                  </>
                )}
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Total columns:</span>
                  <span className={styles.statValue}>{dataset.schema.columns.length}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Selected columns:</span>
                  <span className={styles.statValue}>{selectedColumns?.length || 0}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Selected rows:</span>
                  <span className={styles.statValue}>{selectedRows?.length || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Column Statistics Section */}
        <div className={styles.statisticsSection} data-section-type="column-statistics">
          <h3
            className={`${styles.collapsibleHeader} ${collapsedStates.columnStatistics ? styles.collapsed : ''}`}
            onClick={() => {
              onToggleColumnStatistics();
            }}
          >
            Column Statistics
          </h3>
          {!collapsedStates.columnStatistics && (
            <div className={styles.sectionContent}>
              {!selectedColumns || selectedColumns.length === 0 ? (
                <p>No columns selected. Please select columns in the data table to view statistics.</p>
              ) : (
                <>
                  <ColumnStatistics
                    dataset={dataset}
                    selectedColumns={selectedColumns}
                    filteredRowIndices={filteredRowIndices}
                    selectedRows={selectedRows}
                    collapsedColumnStats={collapsedColumnStats}
                    onToggleColumnStats={onToggleColumnStats}
                    onHistogramBinClick={onHistogramBinClick}
                    onRowSelect={onRowSelect}
                    availableReports={availableReports}
                    onAddToReport={onAddToReport}
                    onCreateNewReport={onCreateNewReport}
                    groupByColumns={groupByColumns}
                  />
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
